import { Observable } from 'rxjs';
import {
  capitalize,
  createAnonymizedName,
  getInitials,
  isTruthy,
  removeDuplicateSpaces,
  Trackable,
} from '../utils';
import { Address } from './address';
import { Application, ApplicationAdapter } from './application';
import { Association } from './association';
import { AssociationStatus } from './association-status';
import { Attachment } from './attachment';
import { Education } from './education';
import { Experience, ExperienceAdapter, ExperienceOverview } from './experience';
import { SocialInfo } from './socialinfo';

const FALSE = 'false';
export const DELETED_CANDIDATE = 'Deleted';
export const GHOST_STATUS = 'Ghost';

export interface Candidate extends Trackable {
  id: number;
  isPartialDto?: boolean;
  isAnonymized?: boolean;
  externalId?: string;
  externalDisplayId?: string;
  firstName: string;
  lastName: string;
  mail?: string;
  visibleMail?: string;
  emailId: string;
  mobile: string;
  altContact?: any;
  status?: string;
  currentJobTitle?: any;
  currentEmployer?: string;
  oneLineIntro?: string;
  skills?: any;
  feedback?: any;
  linkedinUrl?: string;
  currentCompany?: any;
  currentJobLocation?: any;
  expectedSalary?: number;
  summary?: any;
  owner?: any;
  source?: any;
  sourceNote?: any;
  websiteUrl?: string;
  citizenshipStatus?: any;
  experienceInYears?: any;
  languages?: any;
  certifications?: any;
  interestRating?: number;
  experiences?: Experience[];
  educations?: Education[]; // TODO: verify this field is valid as it seems to come from Application
  address?: Address;
  socialInfo?: SocialInfo[];
  createdDate?: string;
  lastModifiedDate: string;
  candidateImageId: number;
  candidateImageUrl: string;
  score?: number;
  keywords?: any[];
  label?: string;
  attachment?: Attachment;
  matchedIrpJobId?: string;
  resumePath?: string;
  favorite?: boolean;
  silver?: boolean;
  alumni?: boolean;
  warning?: boolean;
  createdByName?: string;
  internal?: boolean;
  referralId?: number;
  referredBy?: string;
  talentPoolStatusName?: string;
  name?: string;
  imageId?: number;
  jobApplications?: JobApplications[];
  socialId?: number;
  isShortListed$?: Observable<boolean>;
  latestExperienceEndDate?: number;
  latestExperienceStartDate?: number;
  latestApplication?: Application;
}

export interface JobApplications {
  id: number;
  title: string;
  applicationStatus: ApplicationStatus;
  applicationHistory: AssociationStatus[];
}

export interface ApplicationStatus {
  id: string;
  label: string;
  prospectState: boolean;
  hiredState: boolean;
  position: number;
  association: Association;
  associationSubStatusId: string;
}

const getSafeUtcDate = (utc: number): number => (!!utc ? utc : 0);

export const candidateParser = (
  candidate: Candidate,
  overwriteLabelIfNull: boolean = true,
): Candidate => {
  if (candidate.label == null && overwriteLabelIfNull === false) {
    delete candidate.label;
  }
  return {
    ...candidate,
  };
};

export const candidatesParser = (candidates: Candidate[]): Candidate[] => {
  if (!candidates) {
    return [];
  }
  return candidates.map((candidate: Candidate) => candidateParser(candidate));
};

export class CandidateAdapter {
  constructor(
    public sourceCandidate?: Partial<Candidate>,
    private anonymization: boolean = false,
  ) {}

  get isPartialDto(): boolean {
    return (
      this.sourceCandidate?.isPartialDto &&
      !this.sourceCandidate.experiences &&
      !this.sourceCandidate.latestApplication
    );
  }

  get isNotAnonymized(): boolean {
    return !this.anonymization;
  }

  get id(): number {
    return !!this.sourceCandidate && this.sourceCandidate.id;
  }

  get externalId(): string {
    return !!this.sourceCandidate && this.sourceCandidate.externalId;
  }

  get externalDisplayId(): string {
    return !!this.sourceCandidate && this.sourceCandidate.externalDisplayId;
  }

  get firstName(): string {
    return !!this.sourceCandidate && !!this.sourceCandidate.firstName
      ? this.sourceCandidate.firstName
      : '';
  }

  get lastName(): string {
    return !!this.sourceCandidate && !!this.sourceCandidate.lastName
      ? this.sourceCandidate.lastName
      : '';
  }

  get name(): string {
    return capitalize([this.firstName, this.lastName].filter(isTruthy).join(' '));
  }

  get visibleName(): string {
    return this.anonymization ? createAnonymizedName(this.id, this.initials) : this.name;
  }

  get initials(): string {
    return getInitials(this.name);
  }

  get address(): Address {
    return !!this.sourceCandidate && this.sourceCandidate.address;
  }

  get application(): ApplicationAdapter {
    return new ApplicationAdapter(this.sourceCandidate.latestApplication);
  }

  get educations(): Education[] {
    return !!this.application && !!this.application.source && this.application.source.educations;
  }

  get city(): string {
    return (!!this.address && this.address.city !== FALSE && this.address.city) || '';
  }

  get country(): string {
    return (!!this.address && this.address.country !== FALSE && this.address.country) || '';
  }

  get state(): string {
    return (!!this.address && this.address.state !== FALSE && this.address.state) || '';
  }

  get streetAddress(): string {
    return (
      (!!this.address && this.address.streetAddress !== FALSE && this.address.streetAddress) || ''
    );
  }

  get suiteNumber(): string {
    return (
      (!!this.application && this.application.suiteNumber) ||
      (!!this.address && this.address.suiteNumber)
    );
  }

  get zipCode(): string {
    return !!this.address && this.address.zipCode;
  }

  get location(): string {
    return (
      (!this.city &&
        !this.country &&
        !this.streetAddress &&
        !!this.address &&
        this.address.location !== FALSE &&
        this.address.location) ||
      ''
    );
  }

  get addressLong(): string {
    if (!!this.location) {
      return this.location;
    }
    return [this.streetAddress, this.city, this.state, this.zipCode, this.country]
      .filter(isTruthy)
      .join(', ');
  }

  get addressShort(): string {
    if (!this.city && !this.state && !!this.location) {
      return this.location;
    }
    return [this.city, this.state, this.country].filter(isTruthy).join(', ');
  }

  get experiences(): ExperienceAdapter[] {
    return (
      (!!this.application && this.application.experiences) ||
      (!!this.sourceCandidate && this.sourceCandidate.experiences) ||
      []
    ).map((experience: Experience) => new ExperienceAdapter(experience));
  }

  get candidateImageId(): number {
    return this.anonymization
      ? null
      : !!this.sourceCandidate && this.sourceCandidate.candidateImageId;
  }

  get label(): string {
    return !!this.sourceCandidate && this.sourceCandidate.label;
  }

  get socialInfo(): SocialInfo[] {
    return !!this.sourceCandidate &&
      !!this.sourceCandidate.socialInfo &&
      this.sourceCandidate.socialInfo.length > 0
      ? this.sourceCandidate.socialInfo
      : !!this.sourceCandidate?.latestApplication
      ? this.sourceCandidate.latestApplication.socialInfo
      : [];
  }

  get mail(): string {
    return !!this.sourceCandidate && this.sourceCandidate.emailId;
  }

  get visibleMail(): string {
    return this.anonymization ? 'E-mail' : this.mail;
  }

  get mobile(): string {
    return !!this.sourceCandidate && this.sourceCandidate.mobile;
  }

  get experience(): ExperienceAdapter {
    return !!this.experiences && this.experiences[0];
  }

  get hasExperience(): boolean {
    return (
      !!this.application && !!this.application.totalExp && !!this.application.tenurePerEmployer
    );
  }

  get experienceYears(): number {
    return !!this.application && Math.round(this.application.totalExp / 12);
  }

  get experienceInYears(): number {
    return this.sourceCandidate?.experienceInYears;
  }

  get currentJobTitle(): string {
    if (!!this.sourceCandidate && !!this.sourceCandidate.currentJobTitle) {
      return this.sourceCandidate.currentJobTitle;
    }
    return this.experience?.jobTitle;
  }

  get currentJobLocation(): string {
    return !!this.sourceCandidate && this.sourceCandidate.currentJobLocation;
  }

  get currentCompany(): string {
    return this.sourceCandidate?.currentCompany;
  }

  get createdDate(): Date | string {
    return !!this.sourceCandidate && this.sourceCandidate.createdDate;
  }

  get status(): string {
    return !!this.sourceCandidate && this.sourceCandidate.status;
  }

  get favorite(): boolean {
    return !!this.sourceCandidate && this.sourceCandidate.favorite;
  }

  get silver(): boolean {
    return !!this.sourceCandidate && this.sourceCandidate.silver;
  }

  get alumni(): boolean {
    return !!this.sourceCandidate && this.sourceCandidate.alumni;
  }

  get warning(): boolean {
    return !!this.sourceCandidate && this.sourceCandidate.warning;
  }

  get source(): string {
    return !!this.sourceCandidate && this.sourceCandidate.source;
  }

  get sourceNote(): string {
    return !!this.sourceCandidate && this.sourceCandidate.sourceNote;
  }

  get createdByName(): string {
    return this.sourceCandidate && this.sourceCandidate.createdByName;
  }

  get overview(): ExperienceOverview {
    if (!!this.application) {
      if (!!this.application.summary) {
        return {
          type: 'Summary',
          text: this.application.summary,
        };
      }

      if (!!this.application.objective) {
        return {
          type: 'Objective',
          text: this.application.objective,
        };
      }

      if (!!this.application.qualifications) {
        return {
          type: 'Qualifications',
          text: this.application.qualifications,
        };
      }
    }
    return null;
  }

  get internal(): boolean {
    return this.sourceCandidate?.internal;
  }

  get referredId(): number {
    return this.sourceCandidate?.referralId;
  }

  get referredBy(): string {
    return this.sourceCandidate?.referredBy;
  }

  get latestExperienceStartDate(): number {
    return this.sourceCandidate?.latestExperienceStartDate;
  }

  get latestExperienceEndDate(): number {
    return this.sourceCandidate?.latestExperienceEndDate;
  }

  get talentPoolStatusName(): string {
    return this.sourceCandidate?.talentPoolStatusName;
  }

  set talentPoolStatusName(val: string) {
    this.sourceCandidate.talentPoolStatusName = val;
  }
}

export class CandidateFormAdapter {
  static toCandidate(candidateForm: Partial<CandidateFormAdapter>): Partial<Candidate> {
    return {
      id: candidateForm.id,
      candidateImageId: candidateForm.photoId,
      firstName: removeDuplicateSpaces(candidateForm.firstName),
      lastName: removeDuplicateSpaces(candidateForm.lastName),
      emailId: candidateForm.mail,
      mobile: candidateForm.mobile,
      address: !!candidateForm.locationInput
        ? {
            city: candidateForm.locationInput.name,
            country: candidateForm.locationInput.countryCode,
            state: candidateForm.locationInput.stateCode,
            streetAddress: candidateForm.streetAddress,
            location: candidateForm.locationInput.location,
          }
        : {
            city: null,
            country: null,
            state: null,
            streetAddress: null,
            location: null,
          },
      socialInfo: !!candidateForm.socialInfo
        ? candidateForm.socialInfo
            .filter(({ url }) => !!url)
            .map((socialInfo) => SocialInfo.setType(socialInfo))
        : null,
      source: candidateForm.source,
      sourceNote: candidateForm.sourceNote,
      currentJobTitle: candidateForm.currentJobTitle,
    };
  }

  locationInput: {
    name: string;
    stateCode: string;
    countryCode: string;
    countryName: string;
    location: string;
  };
  id: number;
  photoId: number;
  firstName: string;
  lastName: string;
  source: string;
  sourceNote: string;
  mail: string;
  mobile: string;
  streetAddress: string;
  socialInfo: SocialInfo[];
  currentJobTitle: string;

  constructor(source: Candidate) {
    const candidate = new CandidateAdapter(source);
    this.locationInput = {
      name: candidate.city,
      stateCode: candidate.state,
      countryCode: candidate.country,
      countryName: candidate.country,
      location: candidate.location,
    };
    this.id = candidate.id;
    this.photoId = candidate.candidateImageId;
    this.firstName = candidate.firstName;
    this.lastName = candidate.lastName;
    this.mail = candidate.mail;
    this.mobile = candidate.mobile;
    this.streetAddress = candidate.streetAddress;
    this.socialInfo = candidate.socialInfo;
    this.source = candidate.source;
    this.sourceNote = candidate.sourceNote;
    this.currentJobTitle = candidate.currentJobTitle;
  }
}
